import { CommonModule } from '@angular/common';
import { IWMSSettingsSubWorkspace } from '@wms/settings';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BaseComponent, WmsTypeaheadComponent, WmsToggleComponent } from '@wms/components';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { BuildPreferencesService } from '@wms/settings/build-preferences';
import { WmsMessageComponent } from "../../../components/wms-message/wms-message.component";
import { isValidMobileAppId, isValidAppServerUrl } from "@wms/core";
import { cloneDeep } from 'lodash-es';
import { Subscription, filter } from 'rxjs';
import { DialogService, FileService } from '@wms/app/services';

let preferencesSectionObj = [
    { sectionName: 'applicationproperties', sectionTitle: 'LABEL_MOBILEAPP_PROPERTIES' },
    { sectionName: 'plugins', sectionTitle: 'LABEL_MOBILEAPP_PLUGINS' },
    { sectionName: 'sslpinning', sectionTitle: 'LABEL_HEADER_SSL_PINNING' },
    { sectionName: 'appicon', sectionTitle: 'LABEL_MOBILEAPP_ICON' },
    { sectionName: 'splashscreen', sectionTitle: 'LABEL_MOBILEAPP_SPLASHSCREEN' }
]

@Component({
    standalone: true,
    selector: 'wms-build-preferences',
    templateUrl: './build-preferences.component.html',
    styleUrls: ['./build-preferences.component.less'],
    imports: [CommonModule, FormsModule, RouterModule, WmsTypeaheadComponent, WmsToggleComponent, TooltipModule, WmsMessageComponent]
})

export class BuildPreferencesComponent extends BaseComponent implements IWMSSettingsSubWorkspace, OnInit {
    protected readonly preferencesSectionObj = preferencesSectionObj;
    initialAppConfig: any;

    appConfig: any = {
        name: '',
        author: { name: '' },
        customPlugins: [],
        activePlugins : {},
        sslPinning : {
          domains: {},
          enabled: false
      },
      orientation: ''
    };

    isApplicationPropertiesComponentDirty: boolean = false;
    isPluginsComponentDirty : boolean = false;
    isSSLPinningComponentDirty : boolean = false;
    isAppIconComponentDirty : boolean = false;
    isSplashComponentDirty : boolean = false;

    isDirty: boolean = false;
    selectedProfile = 'development';
    routerSubscription: Subscription;
    selectedTab: string = "applicationproperties";
    fileService: FileService;

    constructor(private router: Router, public BuildPreferencesService: BuildPreferencesService, private dialogService: DialogService) {
        super();
        this.fileService = inject(FileService);
        this.projectService.getRNBuildConfig().then((response: string) => {
            let parsedResponseObj = JSON.parse(response);
            Object.assign(this.appConfig, cloneDeep(parsedResponseObj))
            this.appConfig.buildMode = this.selectedProfile === 'deployment' ? 'PROD' : 'DEV';
            this.initialAppConfig = cloneDeep(this.appConfig)
            this.BuildPreferencesService.updateAppConfigData(this.appConfig);
        });

        this.routerSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: any) => {
        if(event.url.includes('build-preferences')){
            this.selectedTab = event.url.split('/')[3]?.split('?')[0]
        }
      });
    }
    ngOnInit(): void {
        if(this.router.url.includes('build-preferences')){
            this.selectedTab = this.router.url.split('/')[3]?.split('?')[0]
        }
    }

    showPreferences($event: any, preferenceName: string) {
        if (this.selectedTab === 'applicationproperties') {
            this.validateAppProperties()
            if (this.BuildPreferencesService.showErrorMessage)
                return
        }
        const path = '/settings/build-preferences/' + preferenceName
        if(this.isWSDirty()){
            this.eventManager.emitUpdateNestedTab(path)
            const tabName =  preferencesSectionObj.find(({sectionName})=> sectionName === this.selectedTab)?.sectionTitle || ''
            const msg = { message: this.locale.MESSAGE_UNSAVED_CHANGES.replace("${0}", this.locale[tabName] )}
            this.dialogService.open('unsavedChangesDialog_settings', msg)
            $event?.stopPropagation();
            return
        }
        this.router.navigate([path]);
    }
    validateAppProperties(){
        this.BuildPreferencesService.showErrorMessage = false
        if(this.isApplicationPropertiesComponentDirty){
            if (!this.appConfig.name || this.appConfig.name === '') {
                this.BuildPreferencesService.showErrMsg(this.locale['MESSAGE_APPNAME_NOT_SET'])
            }else if(this.appConfig.customUrl !== undefined && !isValidAppServerUrl(this.appConfig.customUrl)){
                this.BuildPreferencesService.showErrMsg(this.locale['MESSAGE_HELP_MOBILE_APPLICATION_SERVER_PATH_INVALID'])
            }else if (!this.appConfig.baseUrl || this.appConfig.baseUrl === ''){
                this.BuildPreferencesService.showErrMsg(this.locale['MESSAGE_BASEURL_NOT_SET'])
            }else if (!this.appConfig.id || !isValidMobileAppId(this.appConfig.id)){
                this.BuildPreferencesService.showErrMsg(this.locale['MESSAGE_HELP_MOBILE_INVALID_APPLICATION_ID'])
            }else if (!this.appConfig.version || this.appConfig.version === ''){
                this.BuildPreferencesService.showErrMsg(this.locale['MESSAGE_APP_VERSION_NOT_SET'])
            }else if (!this.appConfig.author.name || this.appConfig.author.name === ''){
                this.BuildPreferencesService.showErrMsg(this.locale['MESSAGE_DEVELOPER_NAME_NOT_SET'])
            }else if (!this.appConfig.author.email || this.appConfig.author.email === ''){
                this.BuildPreferencesService.showErrMsg(this.locale['MESSAGE_DEVELOPER_EMAIL_NOT_SET'])
            }
        }
      }

      saveChanges() {

        return new Promise((res, rej) => {
            this.validateAppProperties()
            if (this.BuildPreferencesService.showErrorMessage) {
                rej();
                return;
            }
            this.projectService.saveNativeMobileBuildConfig({ 'config': this.appConfig }).then((response: any) => {
                this.BuildPreferencesService.showErrorMessage = false
                this.initialAppConfig = cloneDeep(this.appConfig)
                this.BuildPreferencesService.updateAppConfigData(this.appConfig);
                res(this.appConfig)
            })
        });
    };

    discardChanges() {
        this.isDirty = false;
        this.BuildPreferencesService.showErrorMessage = false
        this.appConfig = cloneDeep(this.initialAppConfig)
        this.BuildPreferencesService.updateAppConfigData(this.appConfig)
    };

    isWSDirty() {
        if (this.isApplicationPropertiesComponentDirty || this.isPluginsComponentDirty || this.isSSLPinningComponentDirty || this.isAppIconComponentDirty || this.isSplashComponentDirty) {
            this.isDirty = true;
        } else {
            this.isDirty = false;
        }
        return this.isDirty;
    };

    override ngOnDestroy(): void {
        this.routerSubscription.unsubscribe();
    }
}
